import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import CloseButton from '@mui/icons-material/Close';
import { INSERT_CODE, BORDER_BACKGROUND, TEXTFIELDMATRIX } from '@/constants';
import { setShowInsertModalState } from '@/actions';
import InlineButtonsGroup from '@/components/InlineButtonsGroup';
import IconBarAndQRCode from '@/icons/icon-bar-and-qr-code.svg';
import IconImagesAndSymbols from '@/icons/icon-images-and-symbols.svg';
import IconShapesAndVectors from '@/icons/icon-shapes-and-vectors.svg';
import IconTextFieldMatrix from '@/icons/icon-textfieldmatrix.svg';
import IconAutofillAndCopy from '@/icons/icon-autofill-and-copy.svg';
import Image from './ImagesAndSymbols/Image';
import Symbol from './ImagesAndSymbols/Symbol';
import MatrixFormatForm from './MatrixFormatForm';
import ShapesAndVectors from './ShapesAndVectors';
import AutofillAndCopyForm from './AutofillAndCopyForm';
import Expandable from './Expandable';
import InsertCodeForm from './InsertCodeForm';

import {
  ItemsContainer,
  InsertContainer,
  NotExpandable,
  TitleLabel,
  GridContainer,
  HeaderContainer,
} from './styles';

const InsertBar = props => {
  const {
    isSmartPhone,
    setSelectedInsertBar,
    selectedInsertBar,
    isMatrix,
    verticalAlignment,
    horizontalAlignment,
  } = props;
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = React.useState(true);
  const [isSymbolTab, setIsSymbolTab] = React.useState(false);
  const isMatrixSelected = selectedInsertBar === TEXTFIELDMATRIX;

  const handleSwitchExpandable = expandableKey => {
    setSelectedInsertBar(expandableKey);
  };

  const insertCodeFormCompRef = useRef();
  const borderBackgroundFormCompRef = useRef();
  const matrixFormatFormCompRef = useRef();
  const autoFillAndCopyFormCompRef = useRef();
  const tabsRefMap = {
    [INSERT_CODE]: insertCodeFormCompRef,
    [BORDER_BACKGROUND]: borderBackgroundFormCompRef,
  };

  const onTabApplyClick = () => {
    try {
      let error = false;

      [
        tabsRefMap[INSERT_CODE],
        ...(isSmartPhone ? [tabsRefMap[BORDER_BACKGROUND]] : []),
      ].forEach(ref => {
        if (ref.current.validate && !ref.current.validate()) {
          error = true;
          console.error(
            `[CodeInsertBar.onApplyClick] Invalid data in ${INSERT_CODE} form`
          );
        } else if (ref.current?.hasChanges()) {
          ref.current?.submit();
        }
      });

      if (!error) {
        handleSetInsertModalClose();
      }
    } catch (ex) {
      console.error('[CodeInsertBar.onApplyClick] ', ex);
    }
  };

  const onMatrixTabApplyClick = () => {
    try {
      if (matrixFormatFormCompRef.current) {
        if (
          matrixFormatFormCompRef.current.validate &&
          !matrixFormatFormCompRef.current.validate()
        ) {
          console.error(
            '[MatrixSideMenu.onApplyClick] Invalid data in Matrix form'
          );
          return;
        }
        if (matrixFormatFormCompRef.current.submit) {
          matrixFormatFormCompRef.current.submit();
        }
      }

      handleSetInsertModalClose();
    } catch (ex) {
      console.error('[TextSideMenu.onApplyClick] ', ex);
    }
  };

  const onAutofillAndCopyabApplyClick = () => {
    try {
      if (autoFillAndCopyFormCompRef.current) {
        if (
          autoFillAndCopyFormCompRef.current.validate &&
          !autoFillAndCopyFormCompRef.current.validate()
        ) {
          console.error(
            '[AutofillSideMenu.onApplyClick] Invalid data in autofillCopyForm form'
          );
          return;
        }
        if (autoFillAndCopyFormCompRef.current.submit) {
          autoFillAndCopyFormCompRef.current.submit();
        }
      }

      handleSetInsertModalClose();
    } catch (ex) {
      console.error('[AutofillSideMenu.onApplyClick] ', ex);
    }
  };

  const handleSetInsertModalClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      dispatch(setShowInsertModalState(false));
      setSelectedInsertBar(null);
    }, 500);
  };

  const barAndQRCodeTools = [
    <InsertCodeForm
      key="barAndQRCodeControlTools-InsertCodeForm"
      onTabApplyClick={onTabApplyClick}
      compRef={insertCodeFormCompRef}
    />,
  ];
  const imagesAndSymbolsTabs = [
    {
      text: <FormattedMessage id="cmme.images" defaultMessage="Images" />,
      selected: !isSymbolTab,
      onClick: () => setIsSymbolTab(false),
    },
    {
      text: <FormattedMessage id="cmme.symbols" defaultMessage="Symbols" />,
      selected: isSymbolTab,
      onClick: () => setIsSymbolTab(true),
    },
  ];
  const imagesAndSymbolsTools = [
    <GridContainer
      key="imagesAndSymbolsTools-grid"
      gridTemplateColumns="1fr"
      gridGap="20px"
    >
      <div className="inputContainerWhite">
        <InlineButtonsGroup
          backgroundColor="transparent"
          items={imagesAndSymbolsTabs}
        />
      </div>
      {isSymbolTab ? (
        <Symbol
          verticalAlignment={verticalAlignment}
          horizontalAlignment={horizontalAlignment}
        />
      ) : (
        <Image
          verticalAlignment={verticalAlignment}
          horizontalAlignment={horizontalAlignment}
        />
      )}
    </GridContainer>,
  ];
  const autofillAndCopyTools = [
    <GridContainer key="autofillAndCopyTools-grid" gridTemplateColumns="1fr">
      <AutofillAndCopyForm
        key="textfieldMatrixTools-AutofillAndCopyForm"
        isMatrix={isMatrix}
        onTabApplyClick={onAutofillAndCopyabApplyClick}
        compRef={autoFillAndCopyFormCompRef}
      />
    </GridContainer>,
  ];

  return (
    <InsertContainer
      isOpen={isOpen}
      className={isOpen ? 'insertBarOpen' : 'insertBarClosed'}
    >
      <ItemsContainer gridTemplateColumns="1fr" overflow="hidden">
        <NotExpandable id="InsertContainer-header" isVisible={true}>
          <HeaderContainer key="gridControlTools-grid">
            <TitleLabel key="gridControlTools-label">
              {isMatrixSelected ? (
                <FormattedMessage
                  id="app.editor.sideMenu.header.matrix"
                  defaultMessage="Matrix"
                />
              ) : (
                <FormattedMessage id="cmme.insert" defaultMessage="Insert" />
              )}
            </TitleLabel>
            <CloseButton
              onClick={() => handleSetInsertModalClose()}
              style={{ color: '#202020', cursor: 'pointer' }}
            />
          </HeaderContainer>
        </NotExpandable>
        <Expandable
          id="BARANDQRCODE"
          icon={IconBarAndQRCode}
          isGrey={true}
          translatedMessageID="cmme.barAndQRCode"
          defaultMessage="Barcodes"
          expanded={selectedInsertBar === 'BARANDQRCODE'}
          visible={!selectedInsertBar || selectedInsertBar === 'BARANDQRCODE'}
          scrollable={true}
          handleSwitchExpandable={handleSwitchExpandable}
          children={barAndQRCodeTools}
          onTabApplyClick={onTabApplyClick}
        />
        <Expandable
          id="IMAGESANDSYMBOLS"
          icon={IconImagesAndSymbols}
          isGrey={true}
          translatedMessageID="cmme.imagesAndSymbols"
          defaultMessage="Images & symbols"
          expanded={selectedInsertBar === 'IMAGESANDSYMBOLS'}
          visible={
            !selectedInsertBar || selectedInsertBar === 'IMAGESANDSYMBOLS'
          }
          scrollable={true}
          handleSwitchExpandable={handleSwitchExpandable}
          children={imagesAndSymbolsTools}
        />
        <Expandable
          id="SHAPESANDVECTORS"
          icon={IconShapesAndVectors}
          isGrey={true}
          translatedMessageID="cmme.shapesAndVectors"
          defaultMessage="Shapes & Vectors"
          expanded={selectedInsertBar === 'SHAPESANDVECTORS'}
          visible={
            !selectedInsertBar || selectedInsertBar === 'SHAPESANDVECTORS'
          }
          scrollable={true}
          handleSwitchExpandable={handleSwitchExpandable}
        >
          <ShapesAndVectors />
        </Expandable>
        <Expandable
          id={TEXTFIELDMATRIX}
          icon={IconTextFieldMatrix}
          isGrey={true}
          translatedMessageID="app.editor.sideMenu.header.matrix"
          defaultMessage="Matrix"
          expanded={isMatrixSelected}
          visible={isMatrixSelected}
          scrollable={true}
          handleSwitchExpandable={handleSwitchExpandable}
          onTabApplyClick={onMatrixTabApplyClick}
        >
          <GridContainer
            key="textfieldMatrixTools-grid"
            gridTemplateColumns="1fr"
          >
            <MatrixFormatForm
              key="textfieldMatrixTools-InsertMatrixForm"
              onTabApplyClick={onMatrixTabApplyClick}
              compRef={matrixFormatFormCompRef}
            />
          </GridContainer>
        </Expandable>
        <Expandable
          id="AUTOFILLANDCOPY"
          icon={IconAutofillAndCopy}
          isGrey={true}
          translatedMessageID="app.editor.sideMenu.header.autofill"
          defaultMessage="Autofill & Copy"
          expanded={selectedInsertBar === 'AUTOFILLANDCOPY'}
          visible={
            !selectedInsertBar || selectedInsertBar === 'AUTOFILLANDCOPY'
          }
          scrollable={true}
          handleSwitchExpandable={handleSwitchExpandable}
          children={autofillAndCopyTools}
          onTabApplyClick={onAutofillAndCopyabApplyClick}
        />
      </ItemsContainer>
    </InsertContainer>
  );
};

export default React.memo(InsertBar);
